import { TaskStageProgressContent } from '@/components/features/tasks/TaskStageProgress'
import { useClient } from '@/context/ClientContext'
import { useInterval } from '@/hooks/useInterval'
import { fetchTasksPaginated } from '@/lib/clientHelpers'
import {
  getTaskRuntime,
  medianComparableRuntimeMs,
  selectPreviousRuns,
} from '@/lib/taskRuntime'
import { cn } from '@/lib/utils'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState } from 'react'

import { Spinner } from '@droidmap/frontend-ui'
import { Task, TaskType } from '@droidmap/project-service-contract'
import type { DroidMapMetadata } from '@droidmap/shared-service-contract'

import { RunComparison } from './RunComparison'
import { SectionLabel } from './SectionLabel'

export { RunComparison } from './RunComparison'
export { SectionLabel } from './SectionLabel'

/**
 * How many recent tasks to pull when looking for previous runs. The list is
 * then filtered down to finished ones with a measured runtime, so this is the
 * search window rather than the number shown.
 */
const HISTORY_PAGE_SIZE = 50

/** How many previous runs to put on screen. */
const PREVIOUS_RUN_LIMIT = 5

export interface TaskRunDetailsProps {
  task: Task
  metadata: DroidMapMetadata
  className?: string
}

/**
 * Run details for one task: how long it has taken against the project's
 * previous runs, and — for a photogrammetry job — the live per-stage
 * breakdown underneath.
 *
 * The comparison is the point. A single duration answers "how long did this
 * take" but not "is that normal", and on this workload the second question is
 * the one people actually have — see {@link RunComparison} for why the
 * baseline is restricted to size-comparable runs.
 */
export function TaskRunDetails({
  task,
  metadata,
  className,
}: TaskRunDetailsProps) {
  const { projectClient } = useClient()

  // Tick only while this run is still accruing time, so the panel is inert on
  // a finished job.
  const [now, setNow] = useState(() => Date.now())
  const runtime = getTaskRuntime(task, now)
  useInterval(() => setNow(Date.now()), runtime.live ? 1_000 : null)

  const { data: history, isLoading } = useQuery({
    queryKey: ['taskRunHistory', task.projectId],
    queryFn: () =>
      fetchTasksPaginated(task.projectId, projectClient, metadata, {
        page: 0,
        pageSize: HISTORY_PAGE_SIZE,
        sortBy: 'created',
        sortDir: 'desc',
      }),
  })

  const previous = useMemo(
    () => selectPreviousRuns(task, history?.items ?? [], PREVIOUS_RUN_LIMIT),
    [task, history],
  )
  const medianComparableMs = useMemo(
    () => medianComparableRuntimeMs(previous),
    [previous],
  )

  return (
    <div className={cn('flex flex-col gap-6', className)}>
      {isLoading ? (
        <div className="flex justify-center p-6">
          <Spinner size="md" className="text-accent" />
        </div>
      ) : (
        <RunComparison
          task={task}
          runtime={runtime}
          previous={previous}
          {...(medianComparableMs !== undefined && { medianComparableMs })}
        />
      )}

      {task.type === TaskType.Photogrammetry && task.referenceId && (
        <div>
          <SectionLabel>Processing stages</SectionLabel>
          {/* Content only — the modal panel is already the card, so the
              titled-card variant would nest one inside another. */}
          <TaskStageProgressContent
            mappingJobId={task.referenceId}
            metadata={metadata}
            className="mt-3"
          />
        </div>
      )}
    </div>
  )
}

export default TaskRunDetails
